import {
  getDate,
  getHours,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  isAfter,
  isBefore,
  isEqual,
  parseISO,
} from "date-fns";
import { ca, es, enGB, enUS, enCA, de, fi, fr, frCH, nl, nlBE } from "date-fns/locale";

/**
 * This function computes a key from the given date (getting only the date part and not the time part).
 * @param {Date} date The date to convert into a key.
 * @return {[number,number,number]} The computed key related to the given date.
 */
export const dateToStringKey = (date) => [getYear(date), getMonth(date), getDate(date)].join(",");

/**
 * This function retrieve and build a date from a string key (taking into account the date part, not the time part).
 * The string gather the year, month and day separated by a comma.
 * @param {string} stringKey The date to convert into a key.
 * @return {Date} The computed date related to the given key.
 */
export const stringKeyToDate = (stringKey) => new Date(
  stringKey.split(",")[0],
  stringKey.split(",")[1],
  stringKey.split(",")[2],
);

/**
 * This function computes a time string from the time part of the given date.
 * @param {Date} date The date to convert into a key.
 * @return {string} The time string in the format of 'HH:MM'.
 */
export const dateToTimeString = (date) => (
  `${getHours(date).toString().padStart(2, "0")}:${getMinutes(date).toString().padStart(2, "0")}`
);

/**
 * This function returns the absolute date that is to say without the hours or minutes (by default set to midnight).
 * First, this function convert the date in the locale timezone of the device
 * and then use the year, month and date passed.
 * @param {Date} date - The date to convert
 * @returns {Date} - The new date set to midnight
 */
export const getAbsoluteDate = (date) => new Date(getYear(date), getMonth(date), getDate(date));

/**
 * This function returns the result of the comparison between two dates.
 * First, this function convert the dates in the locale timezone of the device and then compare both.
 * @param {Date} dateA - The first date to compare
 * @param {Date} dateB - The second date to compare
 * @param {String} comparison - The comparison operator to use to compare dateA and dateB (<, <=, >, >=, ==, !=)
 * @returns {Boolean} - The result of the comparison
 */
export const compareDate = (dateA, dateB, comparison) => {
  switch (comparison) {
    case "<": return isBefore(dateA, dateB);
    case ">": return isAfter(dateA, dateB);
    case "<=": return isBefore(dateA, dateB) || isEqual(dateA, dateB);
    case ">=": return isAfter(dateA, dateB) || isEqual(dateA, dateB);
    case "==": return isEqual(dateA, dateB);
    case "!=": return !isEqual(dateA, dateB);
    default: return false;
  }
};

/**
 * This function returns the result of the comparison between two absolute dates
 * (we don't take care about hours and minutes).
 * First, this function convert the dates in the locale timezone of the device and then use the year, month and date.
 * @param {Date} dateA - The first date to compare
 * @param {Date} dateB - The second date to compare
 * @param {String} comparison - The comparison operator to use to compare dateA and dateB (<, <=, >, >=, ==, !=)
 * @returns {Boolean} - The result of the comparison
 */
export const compareAbsoluteDate = (dateA, dateB, comparison) => {
  const a = new Date(getYear(dateA), getMonth(dateA), getDate(dateA));
  const b = new Date(getYear(dateB), getMonth(dateB), getDate(dateB));
  return compareDate(a, b, comparison);
};

/**
 * This function returnsthe result of the comparison between two times.
 * First, this function convert the dates in the locale timezone of the device and then use the hour, minute and second.
 * @param {Date} dateA - The first date with the time to compare
 * @param {Date} dateB - The second date with to compare
 * @param {String} comparison - The comparison operator to use to compare timeA and timeB (<, <=, >, >=, ==, !=)
 * @returns {Boolean} - The result of the comparison
 */
export const compareAbsoluteTime = (dateA, dateB, comparison) => {
  const a = new Date(2020, 1, 1, getHours(dateA), getMinutes(dateA), getSeconds(dateA));
  const b = new Date(2020, 1, 1, getHours(dateB), getMinutes(dateB), getSeconds(dateB));
  return compareDate(a, b, comparison);
};

/**
 * This function returnsthe result of the comparison between two times.
 * First, this function convert the dates in UTC date and then use the hour, minute and second.
 * @param {Date} dateA - The first date with the time to compare
 * @param {Date} dateB - The second date with to compare
 * @param {String} comparison - The comparison operator to use to compare timeA and timeB (<, <=, >, >=, ==, !=)
 * @returns {Boolean} - The result of the comparison
 */
export const compareAbsoluteUTCTime = (dateA, dateB, comparison) => {
  const a = new Date(2020, 1, 1, dateA.getUTCHours(), dateA.getUTCMinutes(), dateA.getUTCSeconds());
  const b = new Date(2020, 1, 1, dateB.getUTCHours(), dateB.getUTCMinutes(), dateB.getUTCSeconds());
  return compareDate(a, b, comparison);
};

/**
 * Compute whether the current reference time is between the start and end.
 * If start or end is null, we consider it as infinity. If both are null we return false (no period)
 * @param {Date|String} start The start date of the period
 * @param {Date|String} end The end date of the period
 * @param {Date|String} dateReference The reference date to compare
 * @return {boolean} Whether or not the period is active
 */
export const isOngoingPeriod = (start, end, dateReference) => {
  const reference = dateReference instanceof Date
    ? dateReference
    : dateReference && parseISO(dateReference);
  const now = reference || new Date(Date.now());
  const startDate = start instanceof Date ? start : start && parseISO(start);
  const endDate = end instanceof Date ? end : end && parseISO(end);

  if (!startDate && !endDate) {
    return false;
  }
  if (startDate && !endDate) {
    return isAfter(now, startDate);
  }
  if (!startDate && endDate) {
    return isBefore(now, endDate);
  }
  return isBefore(now, endDate) && isAfter(now, startDate);
};

/**
 * Compute whether the current reference time is before the start and end.
 * If start or end is null, we consider it as infinity. If both are null we return false (no period)
 * @param {Date|String} start The start date of the period
 * @param {Date|String} end The end date of the period
 * @param {Date|String} dateReference The reference date to compare
 * @return {boolean} Whether or not the period is in the future
 */
export const isFuturePeriod = (start, end, dateReference) => {
  const reference = dateReference instanceof Date
    ? dateReference
    : dateReference && parseISO(dateReference);
  const now = reference || new Date(Date.now());
  const startDate = start instanceof Date ? start : start && parseISO(start);
  const endDate = end instanceof Date ? end : end && parseISO(end);

  if (!startDate && !endDate) {
    return false;
  }
  if (startDate && !endDate) {
    return isBefore(now, startDate);
  }
  if (!startDate && endDate) {
    return false;
  }
  return isBefore(now, endDate) && isBefore(now, startDate);
};

/**
 * Compute whether the current reference time is after the start and end.
 * If start or end is null, we consider it as infinity. If both are null we return false (no period)
 * @param {Date|String} start The start date of the period
 * @param {Date|String} end The end date of the period
 * @param {Date|String} dateReference The reference date to compare
 * @return {boolean} Whether or not the period is in the past
 */
export const isPastPeriod = (start, end, dateReference) => {
  const reference = dateReference instanceof Date
    ? dateReference
    : dateReference && parseISO(dateReference);
  const now = reference || new Date(Date.now());
  const startDate = start instanceof Date ? start : start && parseISO(start);
  const endDate = end instanceof Date ? end : end && parseISO(end);

  if (!startDate && !endDate) {
    return false;
  }
  if (startDate && !endDate) {
    return false;
  }
  if (!startDate && endDate) {
    return isAfter(now, endDate);
  }
  return isAfter(now, endDate) && isAfter(now, startDate);
};

/**
 * Return the Locale object from the 'date-fns' library to use for date formatting.
 * @param {string} language The language code according to the ISO 639-1 + optional country code
 * @return {Locale} The Locale object from 'date-fns'
 */
export const getDateFnsLocale = (language) => {
  switch (language) {
    case "ca":
    case "ca-ES":
      return ca;
    case "en":
    case "en-GB":
      return enGB;
    case "en-US":
      return enUS;
    case "en-CA":
      return enCA;
    case "es":
    case "es-ES":
      return es;
    case "de":
    case "de-DE":
    case "de-AT":
      return de;
    case "fi":
    case "fi-FI":
      return fi;
    case "fr":
    case "fr-FR":
      return fr;
    case "fr-CH":
      return frCH;
    case "nl":
    case "nl-NL":
      return nl;
    case "nl-BE":
      return nlBE;
    default:
      return enGB;
  }
};

/**
 * This function is used to enable and display only the given dates.
 * @param {array<Date>} datesToEnable The list of the Date to allow.
 * @return {(function(*): (boolean))|*} The function that takes a date and return if it is allowed or not.
 */
export const disableAllDatesExcludingGiven = (datesToEnable) => (date) => {
  if (!datesToEnable || datesToEnable.map((d) => d.getTime()).includes(date.getTime())) {
    return false;
  }
  return true;
};
